"use client"

import { useEffect, useState } from "react"
import { ScheduledNotetakerMeeting } from "@/schema"
import { Checkbox } from "@ui/shad"

import { GenericDialog } from "@/components/dialogs"

interface ScheduledNotetakersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  meetings: ScheduledNotetakerMeeting[]
  pending: boolean
  onConfirm: (meetingIds: string[]) => void
}

export function ScheduledNotetakersDialog({
  open,
  onOpenChange,
  meetings,
  pending,
  onConfirm,
}: ScheduledNotetakersDialogProps) {
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    if (open) setSelected(meetings.map((meeting) => meeting.id))
  }, [open, meetings])

  const allSelected = meetings.length > 0 && selected.length === meetings.length

  return (
    <GenericDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Cancel scheduled bots?"
      description="Select the bots to cancel. Unchecked bots will be retained."
      dismiss={pending ? "none" : "all"}
      action={{
        label: selected.length > 0 ? "Cancel scheduled bots" : "Save changes",
        onClick: () => onConfirm(selected),
        pending,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false), disabled: pending }}
    >
      <label className="flex items-center gap-2 border-b pb-2 text-sm font-medium">
        <Checkbox
          checked={allSelected ? true : selected.length > 0 ? "indeterminate" : false}
          disabled={pending}
          onCheckedChange={(checked) => setSelected(checked === true ? meetings.map((meeting) => meeting.id) : [])}
        />
        Select all
      </label>
      <ul className="max-h-64 divide-y overflow-y-auto text-sm">
        {meetings.map((meeting) => (
          <li key={meeting.id}>
            <label className="flex items-start gap-2 py-2">
              <Checkbox
                className="mt-0.5"
                checked={selected.includes(meeting.id)}
                disabled={pending}
                onCheckedChange={(checked) =>
                  setSelected((current) =>
                    checked === true ? [...current, meeting.id] : current.filter((id) => id !== meeting.id)
                  )
                }
              />
              <span>
                <span className="block font-medium">{meeting.title}</span>
                <span className="text-muted-foreground block text-xs">
                  {meeting.start_time ? `${new Date(meeting.start_time).toLocaleString()} · ` : ""}
                  {meeting.book_name}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </GenericDialog>
  )
}
